'use client';

/**
 * ComposerDatesCard — delivery + RFQ deadline pickers.
 *
 * Reads `useCartStore.{requestedDate, rfqDeadline}`, mutates via
 * `setRequestedDate / setRfqDeadline`. Two date inputs in a 2-col
 * grid.
 *
 * Hides on `mcp` (cost plans aren't time-bound the same way).
 *
 * @see klyde_edge/composition/cards/composer.py — composer_dates spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { useCartStore } from '@/lib/stores/cart-store';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';

export function ComposerDatesCard() {  const activeIntent = useCartStore((s) => s.activeIntent);
  const requestedDate = useCartStore((s) => s.requestedDate);
  const rfqDeadline = useCartStore((s) => s.rfqDeadline);
  const setRequestedDate = useCartStore((s) => s.setRequestedDate);
  const setRfqDeadline = useCartStore((s) => s.setRfqDeadline);

  // Scene-only export — before the mcp early-return (Rules of Hooks).
  // Hidden for mcp; the draft dates still exist in cart-store, so we
  // register them with a hidden flag rather than vanishing the card.
  useCardExport(useMemo(() => ({
    label: 'Dates',
    content_key: 'composer_dates',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: `Response deadline: ${rfqDeadline || '—'} · Delivery: ${requestedDate || '—'}`,
    json: { response_deadline: rfqDeadline || null, delivery: requestedDate || null },
    state: { hidden_for_mcp: activeIntent === 'mcp' },
  }), [rfqDeadline, requestedDate, activeIntent]));

  if (activeIntent === 'mcp') return null;

  const inputClass = cn(
    text.body,
    'w-full h-7 px-2 rounded-md border border-border bg-background',
    'focus:outline-none focus:ring-1 focus:ring-primary',
  );

  return (
    <RailCard title="Dates" icon={Calendar}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={cn(text.muted, 'block mb-1')}>Response Deadline</label>
          <input
            type="date"
            value={rfqDeadline || ''}
            onChange={(e) => setRfqDeadline(e.target.value || null)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={cn(text.muted, 'block mb-1')}>Delivery</label>
          <input
            type="date"
            value={requestedDate || ''}
            onChange={(e) => setRequestedDate(e.target.value || null)}
            className={inputClass}
          />
        </div>
      </div>
    </RailCard>
  );
}
